import { useState, useCallback } from 'react'
import { useAdSupplierGroup } from '@/hooks/useAdSupplierGroups'
import { useAdSupplierGroupUsers } from '@/hooks/useAdSupplierGroupUsers'
import { useUserRoles } from '@/hooks/useUserRoles'
import { useViewMode } from '@/stores/viewMode'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Plus, Users } from 'lucide-react'
import { DataTable } from './data-table'
import { columns } from './columns'
import { AdSupplierGroupUserAddDialog } from './AdSupplierGroupUserAddDialog'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'

export default function AdSupplierGroupUsersPage() {
  const { id: groupId } = useViewMode()
  const { adSupplierGroup, isLoading: isLoadingGroup } = useAdSupplierGroup(groupId)
  const [showAddDialog, setShowAddDialog] = useState(false)

  // Pagination state
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const pageSize = 10

  const { adSupplierGroupUsers, pagination, isLoading, isError, mutate } = useAdSupplierGroupUsers({
    page,
    pageSize,
    ad_supplier_group_id: groupId ?? undefined,
    account: search || undefined,
  })

  // Get available roles for this ad supplier group
  const { userRoles: availableRoles } = useUserRoles({
    page: 1,
    pageSize: 100,
    adSupplierGroupId: groupId ?? undefined,
    scopeType: 'ad_supplier_group',
  })

  const handleSearch = useCallback((value: string) => {
    setSearch(value)
    setPage(1)
  }, [])

  if (isError) {
    return <div className="text-center text-destructive">載入使用者列表失敗。請稍後再試。</div>
  }

  if (!groupId) {
    return <div className="text-center text-destructive">未選擇廣告商集團</div>
  }

  if (isLoadingGroup && !adSupplierGroup) {
    return (
      <div>
        <Skeleton className="h-8 w-[200px] mb-4" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  const totalPages = pagination?.totalPages ?? 1

  return (
    <div>
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">使用者管理</h2>
          <p className="text-muted-foreground mt-2">
            {adSupplierGroup?.group_name ?? '廣告商集團'} - 管理集團使用者
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)}>
          <Plus className="mr-2 h-4 w-4" />
          新增使用者
        </Button>
      </div>

      <AdSupplierGroupUserAddDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onSuccess={() => {
          mutate()
          setShowAddDialog(false)
        }}
        availableRoles={availableRoles}
      />

      <div className="h-4" />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            集團使用者
          </CardTitle>
          <CardDescription>目前集團共有 {pagination?.total ?? 0} 位使用者</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={adSupplierGroupUsers ?? []}
            onSearch={handleSearch}
            isLoading={isLoading}
            meta={{
              reloadData: mutate,
              availableRoles: availableRoles,
            }}
          />

          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                    .map((p, idx, arr) => {
                      const prevPage = arr[idx - 1]
                      const showEllipsis = prevPage && p - prevPage > 1

                      return (
                        <div key={p} className="flex items-center">
                          {showEllipsis && (
                            <PaginationItem>
                              <span className="px-2">...</span>
                            </PaginationItem>
                          )}
                          <PaginationItem>
                            <PaginationLink
                              onClick={() => setPage(p)}
                              isActive={page === p}
                              className="cursor-pointer"
                            >
                              {p}
                            </PaginationLink>
                          </PaginationItem>
                        </div>
                      )
                    })}

                  <PaginationItem>
                    <PaginationNext
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      className={
                        page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
